import React from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    House, Gauge, Books, FilePdf, PencilLine, GraduationCap,
    Newspaper, Users, ChatCircleDots, SignOut, ShieldCheck, CaretRight, ArrowSquareOut, Buildings, Translate, Notebook,
} from "@phosphor-icons/react";
import { STATECRAFT_LOGO } from "@/lib/brand";

const NAV = [
    { to: "/admin", icon: Gauge, label: "Dashboard", end: true },
    { to: "/admin/repositories", icon: Buildings, label: "Repositories" },
    { to: "/admin/questions", icon: Books, label: "Question Bank" },
    { to: "/admin/pyq", icon: FilePdf, label: "PYQ Papers" },
    { to: "/admin/mains", icon: PencilLine, label: "Mains Prompts" },
    { to: "/admin/study", icon: GraduationCap, label: "Study Material" },
    { to: "/admin/study-plan", icon: Notebook, label: "Study Plan" },
    { to: "/admin/current-affairs", icon: Newspaper, label: "Current Affairs" },
    { to: "/admin/translate", icon: Translate, label: "Translation" },
    { to: "/admin/users", icon: Users, label: "Users" },
    { to: "/admin/feedback", icon: ChatCircleDots, label: "Feedback" },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#F7F9FC] text-slate-900 flex">
            {/* Sidebar */}
            <aside className="w-64 shrink-0 border-r-2 border-[#BFD3FF] bg-white flex flex-col sticky top-0 h-screen">
                <div className="p-5 border-b-2 border-[#BFD3FF]">
                    <div className="flex items-center gap-2">
                        <img src={STATECRAFT_LOGO} alt="Statecraft" className="h-9" />
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#1E3A8A]/25 bg-[#1E3A8A]/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#1E3A8A]">
                        <ShieldCheck size={11} weight="fill" /> Admin Console
                    </div>
                </div>

                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-[#1E3A8A] text-white"
                                        : "text-slate-700 hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A]"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <span className="flex items-center gap-2.5">
                                        <n.icon size={17} weight={isActive ? "fill" : "regular"} />
                                        {n.label}
                                    </span>
                                    <CaretRight size={12} weight="bold" className={isActive ? "opacity-100" : "opacity-40"} />
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="border-t-2 border-[#BFD3FF] p-3 space-y-1">
                    <button
                        data-testid="admin-open-app-btn"
                        onClick={() => navigate("/dashboard")}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        <ArrowSquareOut size={16} weight="bold" /> Open App
                    </button>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                    >
                        <SignOut size={16} weight="bold" /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0">
                <header className="sticky top-0 z-10 border-b-2 border-[#BFD3FF] bg-white/85 backdrop-blur px-8 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Statecraft Console</div>
                        <div className="font-heading font-bold text-lg">Content & Operations</div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                        <span className="text-slate-500 hidden md:inline">Signed in as</span>
                        <span className="font-semibold text-[#1E3A8A]">{user?.email}</span>
                    </div>
                </header>

                <main className="p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
